import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { TESTIMONIALS as DEFAULT_TESTIMONIALS } from "../data/content";
import { useContent } from "../data/ContentContext";

const Avatar = ({ t, size = "lg" }) => {
  const dim = size === "lg" ? "w-16 h-16 text-xl" : "w-10 h-10 text-sm";
  return (
    <div
      className={`${dim} rounded-full flex items-center justify-center font-display tracking-tight shrink-0`}
      style={{ backgroundColor: t.color, color: "#fff" }}
      aria-hidden="true"
    >
      {t.initials}
    </div>
  );
};

const QuoteBody = ({ t }) => (
  <>
    <div className="flex items-center gap-4 mb-4 md:mb-6">
      <Avatar t={t} />
      <div>
        <div className="font-display text-xl md:text-2xl">{t.author}</div>
        <div className="text-xs md:text-sm text-black/65">{t.role}</div>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--accent)] mt-1">
          {t.relationship}
        </div>
      </div>
    </div>
    <p className="text-base md:text-lg lg:text-xl leading-snug font-medium">
      <span className="text-[var(--accent)] text-2xl mr-1">“</span>
      {t.quote}
      <span className="text-[var(--accent)] text-2xl ml-1">”</span>
    </p>
  </>
);

const Testimonials = () => {
  const { content } = useContent();
  const TESTIMONIALS = content?.testimonials || DEFAULT_TESTIMONIALS;
  // Index of testimonial that is currently open on mobile (inline expand).
  // On desktop, this also represents the one shown in the right-hand panel.
  const [i, setI] = useState(0);

  const togglemobile = (idx) => setI((prev) => (prev === idx ? -1 : idx));

  return (
    <section
      id="testimonials"
      className="relative py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[var(--surface)]"
      data-testid="testimonials-section"
    >
      <div className="max-w-[1440px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-12 gap-6 mb-12 md:mb-16"
        >
          <div className="col-span-12 md:col-span-7">
            <div className="overline mb-4">— Word on the street</div>
            <h2 className="editorial-h2 text-5xl md:text-7xl">What people say.</h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 flex items-end">
            <p className="text-base md:text-lg leading-relaxed text-black/70">
              Selected testimonials. Tap a name to read the full quote.
            </p>
          </div>
        </motion.div>

        {/* MOBILE: accordion list — each name expands its quote inline */}
        <ul className="lg:hidden space-y-2.5" data-testid="testimonial-menu">
          {TESTIMONIALS.map((tt, idx) => {
            const active = idx === i;
            return (
              <li
                key={idx}
                className={`border ${active ? "border-black" : "border-black/15"} bg-[var(--bg)] overflow-hidden`}
              >
                <button
                  onClick={() => togglemobile(idx)}
                  className={`w-full text-left flex items-center gap-4 p-3 ${
                    active ? "bg-[var(--ink)] text-[var(--bg)]" : ""
                  }`}
                  data-testid={`testimonial-menu-item-${idx}`}
                  aria-expanded={active}
                >
                  <Avatar t={tt} size={active ? "lg" : "sm"} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base leading-tight truncate">{tt.author}</div>
                    <div className={`text-xs mt-0.5 truncate ${active ? "opacity-80" : "text-black/60"}`}>
                      {tt.role}
                    </div>
                  </div>
                  <ChevronDown
                    size={18}
                    className={`transition-transform shrink-0 ${active ? "rotate-180" : ""}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {active && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="p-5">
                        <QuoteBody t={tt} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>
            );
          })}
        </ul>

        {/* DESKTOP: side menu + right quote panel */}
        <div className="hidden lg:grid grid-cols-12 gap-10">
          <div className="col-span-5">
            <ul
              className="border border-black/15 bg-[var(--bg)]/40 p-2 space-y-1.5 max-h-[520px] overflow-y-auto"
              data-testid="testimonial-menu-desktop"
            >
              {TESTIMONIALS.map((tt, idx) => {
                const active = idx === i;
                return (
                  <li key={idx}>
                    <button
                      onClick={() => setI(idx)}
                      className={`w-full text-left flex items-center gap-4 p-3.5 border transition-colors ${
                        active
                          ? "border-black bg-[var(--ink)] text-[var(--bg)]"
                          : "border-black/15 hover:border-black/60 bg-[var(--bg)]"
                      }`}
                      data-testid={`testimonial-menu-item-desktop-${idx}`}
                    >
                      <Avatar t={tt} size={active ? "lg" : "sm"} />
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-lg leading-tight truncate">{tt.author}</div>
                        <div className={`text-xs mt-0.5 truncate ${active ? "opacity-80" : "text-black/60"}`}>
                          {tt.role}
                        </div>
                      </div>
                      {active && (
                        <span className="text-xs uppercase tracking-[0.2em] font-semibold opacity-80 ml-2">
                          Reading
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="col-span-7" data-testid="testimonial-panel">
            <AnimatePresence mode="wait">
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="bg-[var(--bg)] border border-black/15 p-6 md:p-10"
              >
                <QuoteBody t={TESTIMONIALS[Math.max(0, i)]} />
              </motion.article>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
